import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import cn from 'classnames';
import useAxios from 'axios-hooks';
import Chart from 'react-google-charts';
import groupBy from 'lodash.groupby';
import meanBy from 'lodash.meanby';
import minBy from 'lodash.minby';
import maxBy from 'lodash.maxby';
import numeral from 'numeral';
import { startOfDay, subDays, format, addDays } from 'date-fns';

import { Button } from '../../../components';
import { short } from '../common/formatting/date';

const displayValueWithUnit = ({ value, unit }) => {
	const start = unit.indexOf('{');
	const end = unit.indexOf('}');
	return unit.replace(unit.substring(start, end + 1), value);
};

const RPMDataModal = ({ isOpen, onCancel, rpmData = {}, providerId, onAcknowledge, onReply }) => {
	const [evisit_recommended, setEvisitRecommended] = useState(false);
	const [response, setResponse] = useState('');

	let [{ data, loading: dataLoading, /* error */ }, loadRPMDetailData] = useAxios({
		url: `/providers/${providerId}/vital-tracker-dashboard-patient-detail`,
	}, { manual: true });
	const [{ data: aggData, loading: aggDataLoading, /* error */ }, loadAggregateData] = useAxios({
		url: `/providers/${providerId}/vital-tracker-patient-aggregate`,
		params: {
			patientId: rpmData.patientId,
			date_start: subDays(startOfDay(new Date()), 8),
			date_end: startOfDay(new Date()),
			vitalTypes: [rpmData.vital_name]
		}
	}, { manual: true });

	useEffect(() => {
		if (isOpen && rpmData) {
			const params = {
				...rpmData,
				patient_id: rpmData.patientId,
				date_start: rpmData.vital_date,
				date_end: format(addDays(new Date(rpmData.vital_date), 2), 'yyyy-MM-dd'),
				vitalTypes: [rpmData.vital_name]
			};
			delete params.birth_dttm;
			delete params.gender;
			delete params.name_first;
			delete params.name_last;
			delete params.patientId;
			delete params.profile_image_name;
			delete params.vital_date;
			delete params.vital_name;
			loadRPMDetailData({ params });
			loadAggregateData();
		}
	}, [isOpen, rpmData]);

	const handleAcknowledge = () => {
		onAcknowledge(data[0].vital_id);
	};

	const handleRespond = () => {
		onReply({
			vital_id: data[0].vital_id,
			evisit_recommended,
			provider_response: response
		});
	};

	let groupedAggData;
	if (aggData) {
		groupedAggData = groupBy(aggData, 'vital_name');
		Object.keys(groupedAggData).forEach((vitalName) => {
			groupedAggData[vitalName] = groupBy(groupedAggData[vitalName], 'aggregate_type');
			Object.keys(groupedAggData[vitalName]).forEach((aggregateType) => {
				if (aggregateType === 'min') {
					groupedAggData[vitalName][aggregateType] = {
						value: numeral(minBy(groupedAggData[vitalName][aggregateType].map(({ value }) => ({ value: parseFloat(value) })), 'value').value).format('0.0'),
						unit: groupedAggData[vitalName][aggregateType][0].unit
					};
				} else if (aggregateType === 'max') {
					groupedAggData[vitalName][aggregateType] = {
						value: numeral(maxBy(groupedAggData[vitalName][aggregateType].map(({ value }) => ({ value: parseFloat(value) })), 'value').value).format('0.0'),
						unit: groupedAggData[vitalName][aggregateType][0].unit
					};
				} else {
					groupedAggData[vitalName][aggregateType] = {
						value: numeral(meanBy(groupedAggData[vitalName][aggregateType].map(({ value }) => ({ value: parseFloat(value) })), 'value')).format('0.0'),
						unit: groupedAggData[vitalName][aggregateType][0].unit
					};
				}
			});
		});
	}

	return (
		<Modal
			isOpen={isOpen}
			contentLabel="RPM Data"
			onRequestClose={onCancel}
			className={cn('modal-dialog', 'rpm-data-modal', 'modal-xl')}
		>
			<div className="modal-content">
				<div className="modal-header">
					<button type="button" data-dismiss="modal" aria-label="Close" className="close" onClick={onCancel}>
						<em className="fa fa-times" />
					</button>
					<h4 className="modal-title">{rpmData.name_first} {rpmData.name_last} {rpmData.vital_name} data for {short(rpmData.vital_date)}</h4>
				</div>
				<div className="modal-body">
					<div className="chart-container">
						{Array.isArray(data) && (data.length > 0) && (
							<Chart
								width={800}
								height={400}
								chartType="ScatterChart"
								loader={<div>Loading Chart</div>}
								data={[
									['Time of Day', rpmData.vital_name],
									...data.map(response => [new Date(response.vital_dttm), parseFloat(response.value)])
								]}
							/>
						)}
						{Array.isArray(data) && (data.length == 0) && (
							<div>
								No data found
							</div>
						)}
						{groupedAggData && groupedAggData[rpmData.vital_name] && (
							<div className="aggregate-container">
								{groupedAggData[rpmData.vital_name].avg && (
									<dl>
										<dt>7 Day Average</dt>
										<dd>{displayValueWithUnit(groupedAggData[rpmData.vital_name].avg)}</dd>
									</dl>
								)}
								{groupedAggData[rpmData.vital_name].min && (
									<dl>
										<dt>7 Day Min</dt>
										<dd>{displayValueWithUnit(groupedAggData[rpmData.vital_name].min)}</dd>
									</dl>
								)}
								{groupedAggData[rpmData.vital_name].max && (
									<dl>
										<dt>7 Day Max</dt>
										<dd>{displayValueWithUnit(groupedAggData[rpmData.vital_name].max)}</dd>
									</dl>
								)}
							</div>
						)}
					</div>
					<div>
						<div className="form-group">
							<label className="control-label pr mt-sm">Suggest Visit</label>
							<label className="switch">
								<input
									type="checkbox"
									checked={evisit_recommended}
									onChange={() => setEvisitRecommended(!evisit_recommended)}
								/>
								<span />
							</label>
						</div>
						<div className="form-group">
							<label>Vitals Result Response</label>
							<textarea
								className="form-control"
								placeholder="Please reply to the patient based on the results from the vitals tracking. Patient will receive a notification and email via Care Convene with your request."
								value={response}
								onChange={(event) => setResponse(event.target.value)}
							/>
						</div>
					</div>
				</div>
				<div className="modal-footer">
					<Button
						primary
						large
						onClick={handleRespond}
						disabled={(dataLoading || aggDataLoading) || (Array.isArray(data) && data.length < 1)}
						title="Respond to Patient"
					/>
					<Button
						className="ml"
						large
						danger
						onClick={handleAcknowledge}
						disabled={(dataLoading || aggDataLoading) || (Array.isArray(data) && data.length < 1)}
						title="Acknowledge & Close"
					/>
					<Button
						className="ml"
						large
						title="Close"
						onClick={onCancel}
					/>
				</div>
			</div>
		</Modal>
	);
};

RPMDataModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onCancel: PropTypes.func.isRequired,
	rpmData: PropTypes.object,
	providerId: PropTypes.number.isRequired,
	onAcknowledge: PropTypes.func.isRequired,
	onReply: PropTypes.func.isRequired,
};

export default RPMDataModal;
